import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { groupBy } from 'lodash';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, SlideToggle } from '@ygtechnologies/rubus-ui';
import { ResidentialUnitExtraData } from '@/types';
import { bulkUpdateResidentialUnitReadonly } from '@/services/residential-units';
import theme from './BulkEditReadonlyModal.module.scss';
import { Trans } from '@lingui/react/macro';

interface BulkEditReadonlyModalProps {
    isOpen: boolean;
    residentialUnits: ResidentialUnitExtraData[];
    onClose(): void;
    onSuccess(): void;
}

export const BulkEditReadonlyModal: React.FC<BulkEditReadonlyModalProps> = ({
    isOpen,
    residentialUnits,
    onClose,
    onSuccess,
}) => {
    const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>([]);
    const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [groupedUnits, setGroupedUnits] = useState<any[]>([]);
    const [isReadOnlyForBuyers, setIsReadOnlyForBuyers] = useState(false);

    useEffect(() => {
        if (isOpen && residentialUnits.length > 0) {
            const grouped = groupBy(residentialUnits, (unit) => unit.residentialUnitGroupId || 'Unassigned');
            const groupsArray = Object.entries(grouped).map(([groupId, units]) => ({
                groupId,
                residentialUnitGroupName: units[0]?.residentialUnitGroupName || 'Unassigned',
                units: units.map((unit) => ({
                    residentialUnitId: unit.id,
                    residentialUnitName: unit.name,
                    isReadOnlyForBuyers: unit.isReadOnlyForBuyers,
                })),
            }));

            setGroupedUnits(groupsArray);
            setSelectedUnitIds(residentialUnits.map((unit) => unit.id));

            // Expand all groups by default
            setExpandedGroupIds(groupsArray.map((group) => group.groupId));
        }
    }, [isOpen, residentialUnits]);

    const toggleGroup = (groupId: string) => {
        setExpandedGroupIds((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    };

    const isGroupChecked = (groupId: string) => {
        const group = groupedUnits.find((g) => g.groupId === groupId);
        return group?.units.every((unit) => selectedUnitIds.includes(unit.residentialUnitId)) ?? false;
    };

    const isGroupIndeterminate = (groupId: string) => {
        const group = groupedUnits.find((g) => g.groupId === groupId);
        if (!group) return false;
        const selectedInGroup = group.units.filter((unit) => selectedUnitIds.includes(unit.residentialUnitId)).length;
        return selectedInGroup > 0 && selectedInGroup < group.units.length;
    };

    const handleGroupCheckboxChange = (groupId: string) => {
        const group = groupedUnits.find((g) => g.groupId === groupId);
        if (!group) return;

        const groupUnitIds = group.units.map((unit) => unit.residentialUnitId);
        const allSelected = groupUnitIds.every((id) => selectedUnitIds.includes(id));

        setSelectedUnitIds((prev) => {
            if (allSelected) {
                return prev.filter((id) => !groupUnitIds.includes(id));
            } else {
                const newIds = groupUnitIds.filter((id) => !prev.includes(id));
                return [...prev, ...newIds];
            }
        });
    };

    const handleUnitCheckboxChange = (unitId: number) => {
        setSelectedUnitIds((prev) => {
            if (prev.includes(unitId)) {
                return prev.filter((id) => id !== unitId);
            } else {
                return [...prev, unitId];
            }
        });
    };

    const handleSubmit = async () => {
        if (selectedUnitIds.length === 0) {
            Toast.error(<Trans>Please select at least one unit to update.</Trans>);
            return;
        }

        setIsSaving(true);
        try {
            await bulkUpdateResidentialUnitReadonly({
                residentialUnitIds: selectedUnitIds,
                isReadOnlyForBuyers,
            });

            Toast.success(
                <Trans>
                    Successfully updated {selectedUnitIds.length} unit{selectedUnitIds.length !== 1 ? 's' : ''}
                </Trans>
            );

            onSuccess();
            onClose();
        } catch (error: any) {
            Toast.error(<Trans>Failed to update units. Please try again.</Trans>);
        } finally {
            setIsSaving(false);
        }
    };

    const selectedUnitsCount = selectedUnitIds.length;
    const totalUnitsCount = residentialUnits.length;

    return (
        <Modal size="lg" isOpen={isOpen} toggle={onClose}>
            <ModalHeader>
                <div className={theme.modalHeader}>
                    <Trans>Bulk Edit Readonly Status</Trans>
                </div>
            </ModalHeader>
            <ModalBody>
                <div className={theme.settingsSection}>
                    <div className={theme.toggleSection}>
                        <SlideToggle
                            checked={isReadOnlyForBuyers}
                            onChange={(e) => setIsReadOnlyForBuyers(e.target.checked)}>
                            <Trans>Set units as readonly for buyers</Trans>
                        </SlideToggle>
                    </div>

                    <div className={theme.selectAllSection}>
                        <Checkbox
                            checked={selectedUnitsCount === totalUnitsCount}
                            indeterminate={selectedUnitsCount > 0 && selectedUnitsCount < totalUnitsCount}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedUnitIds(residentialUnits.map((unit) => unit.id));
                                } else {
                                    setSelectedUnitIds([]);
                                }
                            }}>
                            <Trans>
                                Select all units ({selectedUnitsCount} of {totalUnitsCount} selected)
                            </Trans>
                        </Checkbox>
                    </div>
                </div>

                <div className={theme.groupsContainer}>
                    {groupedUnits.map((group) => (
                        <div key={group.groupId} className={theme.groupContainer}>
                            <div className={theme.groupHeader}>
                                <div className={theme.unitGroup} onClick={() => toggleGroup(group.groupId)}>
                                    <FontAwesomeIcon
                                        icon={expandedGroupIds.includes(group.groupId) ? faChevronUp : faChevronDown}
                                        className={theme.chevronIcon}
                                    />
                                    <div className={theme.groupName}>{group.residentialUnitGroupName}</div>
                                </div>
                                <div className={theme.checkboxContainer} onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        checked={isGroupChecked(group.groupId)}
                                        indeterminate={isGroupIndeterminate(group.groupId)}
                                        onChange={() => handleGroupCheckboxChange(group.groupId)}>
                                        <Trans>Select group</Trans>
                                    </Checkbox>
                                </div>
                            </div>

                            {expandedGroupIds.includes(group.groupId) && (
                                <div className={theme.unitsContainer}>
                                    {group.units.map((unit) => (
                                        <div
                                            key={unit.residentialUnitId}
                                            className={theme.unitNameAndCheckbox}
                                            onClick={() => handleUnitCheckboxChange(unit.residentialUnitId)}>
                                            <Checkbox
                                                checked={selectedUnitIds.includes(unit.residentialUnitId)}
                                                onChange={() => handleUnitCheckboxChange(unit.residentialUnitId)}>
                                                <div className={theme.unitNameContainer}>
                                                    <span>{unit.residentialUnitName}</span>
                                                    {unit.isReadOnlyForBuyers && (
                                                        <span className={theme.readonlyIndicator}>
                                                            <Trans>(currently readonly)</Trans>
                                                        </span>
                                                    )}
                                                </div>
                                            </Checkbox>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button className={theme.cancelButton} variant="tertiary" onClick={onClose} disabled={isSaving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button variant="primary" onClick={handleSubmit} loading={isSaving} disabled={selectedUnitIds.length === 0}>
                    <Trans>
                        Update {selectedUnitIds.length} unit{selectedUnitIds.length !== 1 ? 's' : ''}
                    </Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};
